import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, RotateCcw, Lightbulb } from 'lucide-react';
import { useCourse } from '../hooks/useCourse';
import { useAuth } from '../hooks/useAuth';
import { logActivity, saveAiFeedback, getWrongQuestionsHistory, supabase } from '../lib/supabase';
import { TransformQuestion } from '../types';
import { LoadingSpinner } from './LoadingSpinner';
import { getCourseProgress, getNextRecommendation, Recommendation } from '../lib/recommendations';
import { RecommendationCard } from './RecommendationCard';
import { callClaude } from '../lib/claude';

interface ParsedFeedback {
  PUNKTE: string;
  FEEDBACK: string;
  KORREKTE_LOESUNG: string;
  TIPP: string;
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Smart question selection with wrong-answer priority and rotation
function selectSmartQuestions<T extends { id: string }>(
  questions: T[],
  wrongHistory: string[],
  lastQuizQuestionIds: string[],
  count: number
): T[] {
  const questionWeights = questions.map(q => {
    const wrongCount = wrongHistory.filter(id => id === q.id).length;
    const wasInLastQuiz = lastQuizQuestionIds.includes(q.id);

    let weight = 1;
    if (wrongCount >= 3) weight = 4;
    else if (wrongCount >= 2) weight = 3;
    else if (wrongCount === 1) weight = 2;

    if (wasInLastQuiz) weight = Math.max(0.5, weight - 2);
    if (wrongCount === 0 && wasInLastQuiz) weight = 0.3;

    return { question: q, weight };
  });

  const selected: T[] = [];
  const remaining = [...questionWeights];

  while (selected.length < count && remaining.length > 0) {
    const totalWeight = remaining.reduce((sum, qw) => sum + qw.weight, 0);
    let random = Math.random() * totalWeight;
    let selectedIndex = 0;

    for (let i = 0; i < remaining.length; i++) {
      random -= remaining[i].weight;
      if (random <= 0) {
        selectedIndex = i;
        break;
      }
    }

    selected.push(remaining[selectedIndex].question);
    remaining.splice(selectedIndex, 1);
  }

  return shuffleArray(selected);
}

export function TransformQuiz() {
  const { courseId } = useParams<{ courseId: string }>();
  const { course, isLoading, error } = useCourse(courseId || '');
  const { user } = useAuth();

  const [quizKey, setQuizKey] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<ParsedFeedback | null>(null);
  const [rawResponse, setRawResponse] = useState<string>('');
  const [apiError, setApiError] = useState<string | null>(null);
  const [results, setResults] = useState<{ questionId: string; feedback: ParsedFeedback; raw: string }[]>([]);
  const [isFinished, setIsFinished] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [startTime] = useState(Date.now());
  const [wrongHistory, setWrongHistory] = useState<string[]>([]);
  const [lastQuizIds, setLastQuizIds] = useState<string[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);

  // Load wrong question history
  useEffect(() => {
    async function loadHistory() {
      if (!user?.id || !courseId) {
        setHistoryLoaded(true);
        return;
      }

      const history = await getWrongQuestionsHistory(user.id, courseId, 'transform');
      setWrongHistory(history.wrongHistory);
      setLastQuizIds(history.lastQuizQuestionIds);
      setHistoryLoaded(true);
    }

    loadHistory();
  }, [user?.id, courseId, quizKey]);

  // Smart question selection
  const questions = useMemo<TransformQuestion[]>(() => {
    if (!course?.transformQuestions || !historyLoaded) return [];
    const pflicht = course.transformQuestions.filter(q => q.difficulty === 'pflicht');
    return selectSmartQuestions(pflicht, wrongHistory, lastQuizIds, 8);
  }, [course?.transformQuestions, wrongHistory, lastQuizIds, historyLoaded]);

  const currentQuestion = questions[currentIndex];

  const parseResponse = (text: string): ParsedFeedback => {
    const sections: ParsedFeedback = {
      PUNKTE: '',
      FEEDBACK: '',
      KORREKTE_LOESUNG: '',
      TIPP: '',
    };

    const labels: (keyof ParsedFeedback)[] = ['PUNKTE', 'FEEDBACK', 'KORREKTE_LOESUNG', 'TIPP'];

    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      const labelPattern = label === 'KORREKTE_LOESUNG' ? 'KORREKTE[_\\s]?L[OÖ]SUNG' : label;
      const remainingLabels = labels.slice(i + 1).map(l =>
        l === 'KORREKTE_LOESUNG' ? 'KORREKTE[_\\s]?L[OÖ]SUNG' : l
      );
      const endPattern = remainingLabels.length > 0
        ? `(?=[-•*\\s]*\\**(?:${remainingLabels.join('|')})\\**[:\\s])|$`
        : '$';

      const regex = new RegExp(
        `[-•*\\s]*\\**${labelPattern}\\**[:\\s]+(.+?)${endPattern}`,
        'si'
      );

      const match = text.match(regex);
      if (match && match[1]) {
        let content = match[1].trim();
        content = content.replace(/[-•*]+\s*$/, '').trim();
        sections[label] = content;
      }
    }

    return sections;
  };

  const callClaudeAPI = async (question: TransformQuestion, userAnswer: string): Promise<string> => {
    const typeLabel = question.type === 'statement' ? 'Aussage' :
                      question.type === 'command' ? 'Befehl' : 'Frage';

    const systemPrompt = `Du bist ein freundlicher Englisch-Tutor für Adriana (14, 4. Klasse MS Wien).
Bewerte ihre Umwandlung von Direct Speech zu Indirect Speech.

Prüfe:
1. Korrekte Zeitverschiebung (Tense Shift / Backshift)
2. Korrekte Pronomen-Änderung (I→he/she, my→his/her, your→my)
3. Korrektes Reporting Verb (said/told/asked/advised/warned etc.)
4. Korrekte Satzstruktur (kein Fragesatzbau bei indirekten Fragen)
5. Korrekte Zeitausdrücke (now→then, today→that day, etc.)

Antworte auf DEUTSCH in diesem Format:

PUNKTE: X/${question.maxPoints}
FEEDBACK: Was war gut, was kann besser werden (2-3 Sätze)
KORREKTE LÖSUNG: Die richtige Antwort
TIPP: Ein Merktipp für diese Art von Umwandlung

Sei ermutigend aber ehrlich.`;

    const userMessage = `Art: ${typeLabel}
Direct Speech: ${question.directSpeech}
Sprecher: ${question.speaker}
${question.reportingVerb ? `Zu verwendendes Verb: ${question.reportingVerb}` : ''}

Erwartete Antwort: ${question.expectedAnswer}
Adrianas Antwort: ${userAnswer}

Bewerte ihre Antwort.`;

    const data = await callClaude({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 500,
      messages: [{ role: 'user', content: userMessage }],
      system: systemPrompt,
    }, 'transform-check');

    return data.content[0]?.text || '';
  };

  const handleSubmit = async () => {
    if (!userAnswer.trim() || isSubmitting || !currentQuestion) return;

    setIsSubmitting(true);
    setApiError(null);

    try {
      const aiResponse = await callClaudeAPI(currentQuestion, userAnswer);
      setRawResponse(aiResponse);
      const parsedFeedback = parseResponse(aiResponse);
      setFeedback(parsedFeedback);
      setResults(prev => [...prev, {
        questionId: currentQuestion.id,
        feedback: parsedFeedback,
        raw: aiResponse
      }]);

      // Save to Supabase
      if (user?.id) {
        await saveAiFeedback(user.id, 'transform_quiz', aiResponse, parsedFeedback as unknown as Record<string, string>, {
          courseId: courseId || 'english-test3',
          question: currentQuestion.directSpeech,
          userAnswer: userAnswer,
          scoreInfo: parsedFeedback.PUNKTE || '',
        });
      }
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(i => i + 1);
      setUserAnswer('');
      setFeedback(null);
      setRawResponse('');
      setApiError(null);
      setShowHints(false);
    } else {
      // Calculate total score from all results (including current)
      const allResults = [...results];
      if (feedback) {
        allResults.push({ questionId: currentQuestion.id, feedback, raw: rawResponse });
      }

      let totalEarned = 0;
      let totalMax = 0;
      const wrongQuestions: string[] = [];

      for (const result of allResults) {
        if (result.feedback.PUNKTE) {
          const match = result.feedback.PUNKTE.match(/(\d+)\s*[\/von]\s*(\d+)/i);
          if (match) {
            const earned = parseInt(match[1], 10);
            const max = parseInt(match[2], 10);
            totalEarned += earned;
            totalMax += max;
            // Consider wrong if less than 50% of max points
            if (earned < max * 0.5) {
              wrongQuestions.push(result.questionId);
            }
          }
        }
      }
      const percentage = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : 0;
      const elapsedSeconds = Math.round((Date.now() - startTime) / 1000);
      const allQuestionIds = questions.map(q => q.id);

      if (user?.id && courseId) {
        // Save to quiz_results for smart question selection
        await supabase.from('quiz_results').insert({
          user_id: user.id,
          course_id: courseId,
          mode: 'transform',
          score: totalEarned,
          total: totalMax,
          percentage: percentage,
          wrong_questions: wrongQuestions,
          question_ids: allQuestionIds,
          time_seconds: elapsedSeconds,
        });

        // Log activity
        await logActivity(user.id, 'transform_quiz', {
          course_id: courseId,
          score: totalEarned,
          total: totalMax,
          percentage: percentage,
          questions_answered: questions.length,
          duration_minutes: Math.ceil(elapsedSeconds / 60),
        });

        // Load recommendation for result screen
        if (course) {
          const progress = await getCourseProgress(user.id, courseId, course);
          const rec = getNextRecommendation(courseId, progress, course, {
            justCompleted: 'transform',
            lastScore: percentage,
          });
          setRecommendation(rec);
        }
      }
      setIsFinished(true);
    }
  };

  const restart = () => {
    setQuizKey(k => k + 1);
    setCurrentIndex(0);
    setUserAnswer('');
    setFeedback(null);
    setRawResponse('');
    setApiError(null);
    setResults([]);
    setIsFinished(false);
    setShowHints(false);
  };

  if (isLoading || !course) {
    return <div className="loading">Laden...</div>;
  }

  if (isSubmitting) {
    return <LoadingSpinner text="KI bewertet..." />;
  }

  if (error) {
    return (
      <div className="error-state">
        <p>{error}</p>
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <p style={{ color: 'rgba(255,255,255,0.6)' }}>Keine Transform-Fragen verfügbar.</p>
        <Link to={`/course/${courseId}`} className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
      </div>
    );
  }

  // Finished screen
  if (isFinished) {
    return (
      <div style={{ padding: '16px', paddingBottom: '100px' }}>
        <h1 style={{ color: '#fff', fontSize: '24px', textAlign: 'center', marginBottom: '8px' }}>
          Indirect Speech fertig!
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', marginBottom: '24px' }}>
          Du hast {results.length} Sätze umgewandelt
        </p>

        <div style={{ marginBottom: '24px' }}>
          {results.map((result, index) => {
            const question = questions.find(q => q.id === result.questionId);
            return (
              <div key={result.questionId} style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                }}>
                  <span style={{ color: '#9fa8da', fontSize: '13px' }}>
                    Satz {index + 1}
                  </span>
                  <span style={{ color: '#ffd54f', fontSize: '13px', fontWeight: 600 }}>
                    {result.feedback.PUNKTE || '—'}
                  </span>
                </div>
                <p style={{ color: '#e8eaf6', fontSize: '14px', margin: 0 }}>
                  {question?.directSpeech}
                </p>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={restart}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              background: '#ffd54f',
              border: 'none',
              borderRadius: '12px',
              color: '#0a0e27',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            <RotateCcw size={20} />
            Nochmal
          </button>
          <Link
            to={`/course/${courseId}`}
            style={{
              padding: '16px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '12px',
              color: '#e8eaf6',
              fontSize: '16px',
              fontWeight: 600,
              textDecoration: 'none',
              textAlign: 'center',
            }}
          >
            Zurück zum Kurs
          </Link>
        </div>

        {/* Recommendation */}
        {recommendation && (
          <RecommendationCard recommendation={recommendation} />
        )}
      </div>
    );
  }

  const typeLabel = currentQuestion.type === 'statement' ? 'Aussage' :
                    currentQuestion.type === 'command' ? 'Befehl' : 'Frage';
  const typeColor = currentQuestion.type === 'statement' ? '#4caf50' :
                    currentQuestion.type === 'command' ? '#ff9800' : '#42a5f5';

  return (
    <div style={{ padding: '16px', paddingBottom: '100px' }}>
      <Link
        to={`/course/${courseId}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: '#9fa8da',
          textDecoration: 'none',
          marginBottom: '20px',
        }}
      >
        <ArrowLeft size={18} />
        Zurück
      </Link>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
      }}>
        <span style={{
          background: 'rgba(233, 30, 99, 0.2)',
          color: '#e91e63',
          padding: '6px 12px',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: 600,
        }}>
          Indirect Speech
        </span>
        <span style={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: '14px',
        }}>
          {currentIndex + 1} / {questions.length}
        </span>
      </div>

      {/* Type badge */}
      <div style={{
        display: 'inline-block',
        background: `${typeColor}20`,
        color: typeColor,
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 600,
        marginBottom: '16px',
      }}>
        {typeLabel}
      </div>

      {/* Instruction */}
      <p style={{
        color: '#ffd54f',
        fontSize: '15px',
        fontWeight: 600,
        marginBottom: '12px',
      }}>
        {currentQuestion.instruction}
      </p>

      {/* Direct Speech */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.08)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '12px',
      }}>
        <div style={{ color: '#9fa8da', fontSize: '12px', marginBottom: '8px' }}>
          {currentQuestion.speaker} said:
        </div>
        <div style={{
          color: '#fff',
          fontSize: '18px',
          fontWeight: 600,
          fontStyle: 'italic',
        }}>
          {currentQuestion.directSpeech}
        </div>
      </div>

      {/* Reporting verb hint */}
      {currentQuestion.reportingVerb && (
        <div style={{
          color: '#ff9800',
          fontSize: '14px',
          marginBottom: '16px',
        }}>
          Verwende: <strong>{currentQuestion.reportingVerb}</strong>
        </div>
      )}

      {/* User input */}
      <textarea
        value={userAnswer}
        onChange={(e) => setUserAnswer(e.target.value)}
        placeholder="Schreibe den Satz in Indirect Speech..."
        disabled={isSubmitting || !!feedback}
        style={{
          width: '100%',
          minHeight: '100px',
          padding: '14px',
          background: feedback ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: '12px',
          color: '#e8eaf6',
          fontSize: '16px',
          fontFamily: 'inherit',
          resize: 'vertical',
          marginBottom: '16px',
        }}
      />

      {/* Hints */}
      {currentQuestion.hints && currentQuestion.hints.length > 0 && !feedback && (
        <>
          <button
            onClick={() => setShowHints(!showHints)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'none',
              border: 'none',
              color: '#ff9800',
              fontSize: '14px',
              cursor: 'pointer',
              padding: 0,
              marginBottom: '12px',
            }}
          >
            <Lightbulb size={16} />
            {showHints ? 'Hinweise verstecken' : 'Hinweise anzeigen'}
          </button>

          {showHints && (
            <div style={{
              background: 'rgba(255, 152, 0, 0.1)',
              borderRadius: '10px',
              padding: '12px',
              marginBottom: '16px',
            }}>
              {currentQuestion.hints.map((hint, i) => (
                <div key={i} style={{
                  color: '#ff9800',
                  fontSize: '13px',
                  marginBottom: i < currentQuestion.hints!.length - 1 ? '6px' : 0,
                }}>
                  {hint}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Error */}
      {apiError && (
        <div style={{
          background: 'rgba(239, 83, 80, 0.15)',
          border: '1px solid #ef5350',
          borderRadius: '12px',
          padding: '14px',
          marginBottom: '16px',
          color: '#ef5350',
          fontSize: '14px',
        }}>
          {apiError}
        </div>
      )}

      {/* Submit button */}
      {!feedback && (
        <button
          onClick={handleSubmit}
          disabled={!userAnswer.trim() || isSubmitting}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '16px',
            background: userAnswer.trim() && !isSubmitting ? '#ffd54f' : 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '12px',
            color: userAnswer.trim() && !isSubmitting ? '#0a0e27' : 'rgba(255,255,255,0.3)',
            fontSize: '16px',
            fontWeight: 700,
            cursor: userAnswer.trim() && !isSubmitting ? 'pointer' : 'default',
          }}
        >
          <Send size={20} />
          Abschicken
        </button>
      )}

      {/* AI Feedback */}
      {feedback && (
        <div style={{ marginTop: '16px' }}>
          <h3 style={{ color: '#fff', fontSize: '16px', marginBottom: '16px' }}>
            KI-Feedback
          </h3>

          {/* Punkte */}
          {feedback.PUNKTE && (
            <div style={{
              background: 'rgba(255, 213, 79, 0.15)',
              borderLeft: '4px solid #ffd54f',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#ffd54f', fontWeight: 700, fontSize: '13px', marginBottom: '6px' }}>
                PUNKTE
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px' }}>
                {feedback.PUNKTE}
              </div>
            </div>
          )}

          {/* Feedback */}
          {feedback.FEEDBACK && (
            <div style={{
              background: 'rgba(66, 165, 245, 0.15)',
              borderLeft: '4px solid #42a5f5',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#42a5f5', fontWeight: 700, fontSize: '13px', marginBottom: '6px' }}>
                FEEDBACK
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.6' }}>
                {feedback.FEEDBACK}
              </div>
            </div>
          )}

          {/* Korrekte Lösung */}
          {feedback.KORREKTE_LOESUNG && (
            <div style={{
              background: 'rgba(76, 175, 80, 0.15)',
              borderLeft: '4px solid #4caf50',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#4caf50', fontWeight: 700, fontSize: '13px', marginBottom: '6px' }}>
                KORREKTE LÖSUNG
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.6' }}>
                {feedback.KORREKTE_LOESUNG}
              </div>
            </div>
          )}

          {/* Tipp */}
          {feedback.TIPP && (
            <div style={{
              background: 'rgba(255, 152, 0, 0.1)',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '12px',
            }}>
              <div style={{ color: '#ff9800', fontWeight: 700, fontSize: '13px', marginBottom: '6px' }}>
                MERKTIPP
              </div>
              <div style={{ color: '#e8eaf6', fontSize: '15px', lineHeight: '1.6' }}>
                {feedback.TIPP}
              </div>
            </div>
          )}

          {/* Fallback raw */}
          {!feedback.PUNKTE && !feedback.FEEDBACK && rawResponse && (
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '12px',
              padding: '14px 16px',
              color: '#e8eaf6',
              fontSize: '14px',
              whiteSpace: 'pre-wrap',
            }}>
              {rawResponse}
            </div>
          )}

          {/* Next button */}
          <button
            onClick={handleNext}
            style={{
              width: '100%',
              padding: '16px',
              background: '#ffd54f',
              border: 'none',
              borderRadius: '12px',
              color: '#0a0e27',
              fontSize: '16px',
              fontWeight: 700,
              cursor: 'pointer',
              marginTop: '16px',
            }}
          >
            {currentIndex < questions.length - 1 ? 'Nächster Satz' : 'Ergebnis anzeigen'}
          </button>
        </div>
      )}
    </div>
  );
}
